import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Alert, Button, Card, CardContent } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader } from '@/components/common/Loader';
import { PageHeader } from '@/components/common/PageHeader';
import { useNotify } from '@/hooks/useNotify';
import { isApiError } from '@/lib/apiError';

import { ProfesionalForm } from '../components/ProfesionalForm';
import { useProfesional, useUpdateProfesional } from '../hooks/useProfesionales';
import type { ProfesionalFormValues } from '../schemas';
import type { ProfesionalUpdate } from '../types';

export function EditarProfesionalPage(): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();
  const { uidProfesional } = useParams<{ uidProfesional: string }>();

  const { data: profesional, isLoading, isError, error } = useProfesional(uidProfesional);
  const updateMutation = useUpdateProfesional();

  async function handleSubmit(values: ProfesionalFormValues): Promise<void> {
    if (!uidProfesional) return;

    const payload: ProfesionalUpdate = {
      nombre_apellido: values.nombre_apellido.trim(),
      tipo_profesional: values.tipo_profesional,
      tipo_profesional_otro: values.tipo_profesional_otro?.trim() || null,
      telefono_whatsapp: values.telefono_whatsapp?.trim() || null,
      emails: values.emails,
      localidad: values.localidad?.trim() || null,
      envio_informe: values.envio_informe,
    };

    try {
      const updated = await updateMutation.mutateAsync({ uid: uidProfesional, data: payload });
      notify.success(`Cambios guardados en "${updated.nombre_apellido}".`);
      // Decision: stay on edit page after update
    } catch {
      // error displayed inside the form
    }
  }

  if (isLoading) {
    return <Loader message="Cargando profesional..." />;
  }

  if (isError || !profesional) {
    const isNotFound = isApiError(error) && error.isNotFound();
    return (
      <>
        <PageHeader title="Editar profesional" />
        <Alert severity="error">
          {isNotFound
            ? 'El profesional no existe o fue eliminado.'
            : error instanceof Error
              ? error.message
              : 'No se pudo cargar el profesional.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/profesionales')}
          sx={{ mt: 2 }}
        >
          Volver al listado
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={profesional.nombre_apellido}
        subtitle="Editar profesional"
        action={
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/profesionales')}
          >
            Volver
          </Button>
        }
      />

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <ProfesionalForm
            defaultValues={profesional}
            onSubmit={handleSubmit}
            onCancel={() => navigate('/profesionales')}
            submitLabel="Guardar cambios"
            submitting={updateMutation.isPending}
            error={updateMutation.error}
          />
        </CardContent>
      </Card>
    </>
  );
}
